import React, { Component, Suspense, lazy, useContext, useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
  useRouteError,
} from "react-router-dom";
import { AuthProvider, AuthCtx } from "./context/AuthCtx";
import { FilterProvider } from "./context/FilterCtx";
import { ThemeProvider } from "./context/ThemeCtx";
import NotAuthPage from "./components/NotAuthorised";
import RouteGuard from "./components/RouteGuard";
import VersionBanner from "./components/VersionBanner";
import { TALLY_HOME_ORDER } from "./constants/report360Modules";
import { recordPageVisit } from "./services/api";
import {
  getMarketingAnalyticsConsent,
  setMarketingAnalyticsConsent,
  trackMarketingPageView,
} from "./services/marketingAnalytics";

const Login = lazy(() => import("./components/Login"));
const SignupTally = lazy(() => import("./components/SignupTally"));
const PartnerDashboard = lazy(() => import("./components/partner/PartnerDashboard"));
const Report360LandingPreview = lazy(() => import("./components/marketing/Report360LandingPreview"));
const ExpoOffer = lazy(() => import("./components/marketing/ExpoOffer"));
const Report360DemoPage = lazy(() => import("./components/marketing/demo"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const Settings = lazy(() => import("./components/dashboard/Settings"));
const CheckoutPage = lazy(() => import("./components/dashboard/CheckoutPage"));
const Pos = lazy(() => import("./components/dashboard/Pos"));
const Crm = lazy(() => import("./components/dashboard/Crm"));
const Sales = lazy(() => import("./components/dashboard/Sales"));
const SalesChV2 = lazy(() => import("./components/dashboard/SalesChV2"));
const PurchasesExpV2 = lazy(() => import("./components/dashboard/PurchasesExpV2"));
const Procurement = lazy(() => import("./components/dashboard/Procurement"));
const Production = lazy(() => import("./components/dashboard/Production"));
const Inventory = lazy(() => import("./components/dashboard/Inventory"));
const Task = lazy(() => import("./components/dashboard/Task"));
const Employee = lazy(() => import("./components/dashboard/employee"));
const Disciplinary = lazy(() => import("./components/dashboard/Disciplinary"));
const Accounting = lazy(() => import("./components/dashboard/Accounting"));
const Profitability = lazy(() => import("./components/dashboard/ProfitabilityDashboard"));
const GeoRevenueV2 = lazy(() => import("./components/dashboard/GeoRevenueV2"));
const PLV2 = lazy(() => import("./components/dashboard/PLV2"));
const InventoryV2 = lazy(() => import("./components/dashboard/InventoryV2"));
const CashFlowV2 = lazy(() => import("./components/dashboard/CashFlowV2"));
const BalanceSheetV2 = lazy(() => import("./components/dashboard/BalanceSheetV2"));
const ArApV2 = lazy(() => import("./components/dashboard/ArApV2"));
const AccuracyV2 = lazy(() => import("./components/dashboard/AccuracyV2"));
const Gstr2bReconciliation = lazy(() => import("./components/dashboard/Gstr2bReconciliationV2"));

const ODOO_HOME_ORDER = [
  { key: "sales", path: "sales" },
  { key: "crm", path: "crm" },
  { key: "pos", path: "pos" },
  { key: "procurement", path: "procurement" },
  { key: "production", path: "production" },
  { key: "inventory", path: "inventory" },
  { key: "task", path: "task" },
  { key: "employee", path: "employee" },
  { key: "disciplinary", path: "disciplinary" },
  { key: "accounting", path: "accounting" },
  { key: "profitability", path: "profitability" },
];

const ALL_HOME_ORDER = [...TALLY_HOME_ORDER, ...ODOO_HOME_ORDER];

function pickHomeRoute({ tenantType, role, modules }) {
  if (role === "partner") {
    return "partner";
  }
  if (role === "super") {
    return tenantType === "tally" ? "sales-ch" : "sales";
  }

  const modSet = new Set((modules || []).map((moduleKey) => String(moduleKey)));
  const preferred =
    tenantType === "tally" ? TALLY_HOME_ORDER : tenantType === "odoo" ? ODOO_HOME_ORDER : ALL_HOME_ORDER;

  const found = preferred.find((route) => modSet.has(route.key));
  if (found) return found.path;

  const fallback = ALL_HOME_ORDER.find((route) => modSet.has(route.key));
  return fallback ? fallback.path : null;
}

function DashboardHomeRedirect() {
  const { tenantMeta, authReady, role, acl, jwt } = useContext(AuthCtx);

  if (!authReady) return null;
  if (!jwt) return <Navigate to="/login" replace />;
  if (role === "partner") return <Navigate to="/partner" replace />;

  const target = pickHomeRoute({
    tenantType: String(tenantMeta?.tenant_type || "").toLowerCase(),
    role,
    modules: acl?.modules,
  });

  if (!target) {
    return <Navigate to="/dashboard/not-authorised" replace />;
  }

  return <Navigate to={target} replace />;
}

function RedirectIfAuthenticated({ children }) {
  const { authReady, jwt, role } = useContext(AuthCtx);

  if (!authReady) return null;
  if (jwt) return <Navigate to={role === "partner" ? "/partner" : "/dashboard"} replace />;
  return children;
}

function withSuspense(element) {
  return <Suspense fallback={null}>{element}</Suspense>;
}

function inferVisitDeviceType() {
  if (typeof window === "undefined") return "desktop";
  const width = Number(window.innerWidth || 0);
  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  if (/mobi|android|iphone|ipod/.test(ua) || width <= 640) return "mobile";
  if (/ipad|tablet/.test(ua) || width <= 1024) return "tablet";
  return "desktop";
}

function TrackedPublicPage({ children }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    recordPageVisit({
      path: url.pathname,
      query: url.search.startsWith("?") ? url.search.slice(1) : url.search,
      referrer: document.referrer || "",
      campaign: url.searchParams.get("campaign") || url.searchParams.get("utm_campaign") || "",
      ref: url.searchParams.get("ref") || url.searchParams.get("source") || "",
      device_type: inferVisitDeviceType(),
    });
    if (getMarketingAnalyticsConsent() === "granted") {
      setMarketingAnalyticsConsent(true);
      trackMarketingPageView();
    }
  }, []);

  return children;
}

function AnalyticsConsentBanner() {
  const [choice, setChoice] = React.useState(() => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    if (url.searchParams.get("cookie-settings") === "1") return null;
    try {
      return window.localStorage.getItem("report360:analytics-consent");
    } catch {
      return null;
    }
  });

  if (choice) return null;

  const choose = (granted) => {
    if (setMarketingAnalyticsConsent(granted)) {
      if (granted) trackMarketingPageView();
      setChoice(granted ? "granted" : "denied");
      const url = new URL(window.location.href);
      if (url.searchParams.has("cookie-settings")) {
        url.searchParams.delete("cookie-settings");
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
    }
  };

  return (
    <aside
      aria-label="Cookie preferences"
      className="report360-analytics-consent"
      role="dialog"
    >
      <strong>Cookie preferences</strong>
      <p>
        We use optional analytics cookies to understand which Report360 pages
        help visitors. These cookies cannot read your Tally, accounting,
        business, or payment data.
      </p>
      <div>
        <button type="button" onClick={() => choose(true)}>
          Allow
        </button>
        <button type="button" onClick={() => choose(false)}>
          Reject
        </button>
        <a href="/privacy-policy">Privacy &amp; cookie notice</a>
      </div>
    </aside>
  );
}

function isChunkLoadError(error) {
  const message = String(error?.message || error || "");
  const name = String(error?.name || "");
  return name === "ChunkLoadError" || /Loading chunk .* failed|ChunkLoadError/i.test(message);
}

function FriendlyErrorPage({ error }) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError || typeof window === "undefined") return undefined;
    const key = `report360:chunk-reload:${window.location.pathname}`;
    try {
      if (window.sessionStorage.getItem(key) === "1") return undefined;
      window.sessionStorage.setItem(key, "1");
    } catch {
      // If storage is unavailable, a single delayed reload is still better than a stuck route.
    }
    const timer = window.setTimeout(() => window.location.reload(), 250);
    return () => window.clearTimeout(timer);
  }, [chunkError]);

  return (
    <main className="report360-reload-fallback" role="alert">
      <section className="report360-reload-panel">
        <h1>{chunkError ? "A new Report360 version is ready" : "We could not load this page"}</h1>
        <p>
          {chunkError
            ? "Refresh this tab to load the latest version."
            : "Refresh the page and try again. If this keeps happening, contact support."}
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Refresh and try again
        </button>
      </section>
    </main>
  );
}

function RouteErrorFallback() {
  return <FriendlyErrorPage error={useRouteError()} />;
}

function NotFoundPage() {
  return (
    <main className="report360-reload-fallback" role="alert">
      <section className="report360-reload-panel">
        <h1>Page not found</h1>
        <p>The page you opened is not available. Use the home page or sign in to continue.</p>
        <button type="button" onClick={() => window.location.assign("/")}>
          Go to home page
        </button>
      </section>
    </main>
  );
}

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return <FriendlyErrorPage error={error} />;
  }
}

function App() {
  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route errorElement={<RouteErrorFallback />}>
            <Route path="/" element={withSuspense(<TrackedPublicPage><Report360LandingPreview /></TrackedPublicPage>)} />
            <Route path="/demo" element={withSuspense(<TrackedPublicPage><Report360DemoPage /></TrackedPublicPage>)} />
            <Route path="/expo" element={withSuspense(<TrackedPublicPage><ExpoOffer /></TrackedPublicPage>)} />
            <Route path="/expo1" element={withSuspense(<TrackedPublicPage><ExpoOffer focusMode /></TrackedPublicPage>)} />
            <Route path="/prototype/sales-dashboard" element={<Navigate to="/demo" replace />} />
            <Route path="/prototype/purchases-exp-dashboard" element={<Navigate to="/demo" replace />} />
            <Route path="/prototype/geo-dashboard" element={<Navigate to="/demo" replace />} />
            <Route path="/prototype/balance-sheet-dashboard" element={<Navigate to="/demo" replace />} />
            <Route
              path="/login"
              element={withSuspense(
                <RedirectIfAuthenticated>
                  <TrackedPublicPage><Login /></TrackedPublicPage>
                </RedirectIfAuthenticated>,
              )}
            />
            <Route
              path="/sign-up"
              element={withSuspense(
                <RedirectIfAuthenticated>
                  <TrackedPublicPage><SignupTally /></TrackedPublicPage>
                </RedirectIfAuthenticated>,
              )}
            />
            <Route path="/signup" element={<Navigate to="/sign-up" replace />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy.html" replace />} />
            <Route path="/account-deletion" element={<Navigate to="/account-deletion.html" replace />} />
            <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
            <Route path="/checkout" element={<Navigate to="/dashboard/checkout" replace />} />
            <Route path="/landing-preview" element={<Navigate to="/" replace />} />
            <Route path="/landing-preview/demo" element={<Navigate to="/demo" replace />} />
            <Route path="/partner" element={withSuspense(<PartnerDashboard />)} />

            <Route path="/dashboard" element={withSuspense(<DashboardLayout />)}>
              <Route index element={<DashboardHomeRedirect />} />
              <Route path="checkout" element={withSuspense(<CheckoutPage />)} />
              <Route path="settings" element={withSuspense(<Settings />)} />

              <Route path="pos" element={withSuspense(<RouteGuard moduleKey="pos"><Pos /></RouteGuard>)} />
              <Route path="crm" element={withSuspense(<RouteGuard moduleKey="crm"><Crm /></RouteGuard>)} />
              <Route path="sales" element={withSuspense(<RouteGuard moduleKey="sales"><Sales /></RouteGuard>)} />
              <Route path="sales-ch" element={withSuspense(<RouteGuard moduleKey="sales_ch"><SalesChV2 /></RouteGuard>)} />
              <Route path="sales-v2" element={<Navigate to="/dashboard/sales-ch" replace />} />
              <Route path="sales-ch-prototype" element={<Navigate to="/dashboard/sales-ch" replace />} />
              <Route
                path="purchases-exp"
                element={withSuspense(<RouteGuard moduleKey="purchases_exp_tally"><PurchasesExpV2 /></RouteGuard>)}
              />
              <Route
                path="purchases-exp-v2"
                element={<Navigate to="/dashboard/purchases-exp" replace />}
              />
              <Route
                path="procurement"
                element={withSuspense(<RouteGuard moduleKey="procurement"><Procurement /></RouteGuard>)}
              />
              <Route
                path="production"
                element={withSuspense(<RouteGuard moduleKey="production"><Production /></RouteGuard>)}
              />
              <Route
                path="inventory"
                element={withSuspense(<RouteGuard moduleKey="inventory"><Inventory /></RouteGuard>)}
              />
              <Route path="task" element={withSuspense(<RouteGuard moduleKey="task"><Task /></RouteGuard>)} />
              <Route
                path="employee"
                element={withSuspense(<RouteGuard moduleKey="employee"><Employee /></RouteGuard>)}
              />
              <Route
                path="disciplinary"
                element={withSuspense(<RouteGuard moduleKey="disciplinary"><Disciplinary /></RouteGuard>)}
              />
              <Route
                path="accounting"
                element={withSuspense(<RouteGuard moduleKey="accounting"><Accounting /></RouteGuard>)}
              />
              <Route
                path="profitability"
                element={withSuspense(<RouteGuard moduleKey="profitability"><Profitability /></RouteGuard>)}
              />
              <Route path="pl" element={withSuspense(<RouteGuard moduleKey="pl_ch"><PLV2 /></RouteGuard>)} />
              <Route path="pl-v2" element={<Navigate to="/dashboard/pl" replace />} />
              <Route path="geo" element={withSuspense(<RouteGuard moduleKey="geo_ch"><GeoRevenueV2 /></RouteGuard>)} />
              <Route path="geo-v2" element={<Navigate to="/dashboard/geo" replace />} />
              <Route
                path="inventory-ch"
                element={withSuspense(<RouteGuard moduleKey="inventory_ch"><InventoryV2 /></RouteGuard>)}
              />
              <Route
                path="inventory-v2"
                element={<Navigate to="/dashboard/inventory-ch" replace />}
              />
              <Route
                path="cashflow"
                element={withSuspense(<RouteGuard moduleKey="cashflow_direct"><CashFlowV2 /></RouteGuard>)}
              />
              <Route
                path="cashflow-v2"
                element={<Navigate to="/dashboard/cashflow" replace />}
              />
              <Route
                path="balance-ch"
                element={withSuspense(<RouteGuard moduleKey="balance_ch"><BalanceSheetV2 /></RouteGuard>)}
              />
              <Route
                path="balance-v2"
                element={<Navigate to="/dashboard/balance-ch" replace />}
              />
              <Route
                path="arap-ch"
                element={withSuspense(<RouteGuard moduleKey="arap_ch"><ArApV2 /></RouteGuard>)}
              />
              <Route
                path="arap-v2"
                element={<Navigate to="/dashboard/arap-ch" replace />}
              />
              <Route
                path="accuracy"
                element={withSuspense(<RouteGuard moduleKey="accuracy_ch"><AccuracyV2 /></RouteGuard>)}
              />
              <Route
                path="accuracy-v2"
                element={<Navigate to="/dashboard/accuracy" replace />}
              />
              <Route
                path="gstr2b-reco"
                element={withSuspense(<RouteGuard moduleKey="gstr2b_reco"><Gstr2bReconciliation /></RouteGuard>)}
              />

              <Route path="not-authorised" element={<NotAuthPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>,
        ),
        {
          future: {
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          },
        },
      ),
    [],
  );

  return (
    <AuthProvider>
      <ThemeProvider>
        <FilterProvider>
          <>
            <VersionBanner />
            <AnalyticsConsentBanner />
            <AppErrorBoundary>
              <RouterProvider router={router} />
            </AppErrorBoundary>
          </>
        </FilterProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
